import React, { createContext, useContext, useEffect, useState } from 'react';
import { RewardsApi } from '../API/RewardsApi';
import { useAuth } from '../Auth/AuthProvider';
import { RewardType } from '../Types/RewardType';
import { UserType } from '../Types/User';
import { UserReward } from '../Types/UserReward';

interface RewardContextType {
  // Reward counts by type
  rewardCounts: Record<RewardType, number>;

  // Consume a reward of a specific type (returns true if successful)
  registerRewardConsumption: (type: RewardType) => Promise<boolean>;

  // Add rewards to the local state (for when rewards are received)
  registerNewRewards: (rewards: UserReward[]) => void;

  // Force refresh rewards from the server
  refreshRewards: () => Promise<void>;

  // Loading state
  isLoading: boolean;
}

const RewardContext = createContext<RewardContextType | undefined>(undefined);

export const RewardProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [rewards, setRewards] = useState<UserReward[]>();
  const [isLoading, setIsLoading] = useState(true);

  // Calculate counts by reward type
  const rewardCountByType = Object.values(RewardType).reduce(
    (acc, type) => {
      acc[type] = rewards?.filter(
        (reward) => reward.rewardType === type,
      ).length;
      return acc;
    },
    {} as Record<RewardType, number>,
  );

  // Load rewards when user changes
  useEffect(() => {
    if (user?.type === UserType.Student) {
      loadRewards();
    } else {
      setRewards([]);
      setIsLoading(false);
    }
  }, [user?.id]);

  // Fetch rewards from the API
  const loadRewards = async () => {
    setIsLoading(true);
    try {
      const rewardsApi = new RewardsApi();
      const allRewards = await rewardsApi.getUnspentRewards();
      setRewards(allRewards);
    } catch (error) {
      console.error('Failed to fetch rewards:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Consume a reward of a specific type
  const registerRewardConsumption = async (
    type: RewardType,
  ): Promise<boolean> => {
    if (!rewardCountByType[type]) return false;

    try {
      // Find the first reward of the specified type
      const rewardToConsume = rewards?.find(
        (reward) => reward.rewardType === type,
      );
      if (rewardToConsume) {
        // Remove the consumed reward from local state
        setRewards((prevRewards) =>
          prevRewards?.filter((reward) => reward.id !== rewardToConsume.id),
        );
      }
      return true;
    } catch (error) {
      console.error(`Failed to consume ${type} reward:`, error);
      return false;
    }
  };

  // Add rewards to the local state
  const registerNewRewards = (newRewards: UserReward[]) => {
    setRewards((prevRewards) => [
      ...prevRewards.filter((reward) => !newRewards.includes(reward)),
      ...newRewards,
    ]);
  };

  const value = {
    rewardCounts: rewardCountByType,
    registerRewardConsumption: registerRewardConsumption,
    registerNewRewards: registerNewRewards,
    refreshRewards: loadRewards,
    isLoading,
  };

  return (
    <RewardContext.Provider value={value}>{children}</RewardContext.Provider>
  );
};

export const useRewards = (): RewardContextType => {
  const context = useContext(RewardContext);
  if (context === undefined) {
    throw new Error('useRewards must be used within a RewardProvider');
  }
  return context;
};
